<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import type { MenuItem } from 'primevue/menuitem';
import TieredMenu, { type TieredMenuPassThroughOptions } from 'primevue/tieredmenu';
import ListNarrowIcon from '@peanuds/ui/assets/icons/list-narrow.svg?component';
import { LineChartIcon, LogOutIcon, SettingsIcon, Users2Icon } from '@peanuds/ui';
import { authRouteNames, useActiveIdentity, useAuthSessionStore } from '@peanuds/app-shell';
import { type AccountSwitcherItem, useAccountSwitcher } from './useAccountSwitcher';

defineOptions({ name: 'AppAccountSwitcher' });

const emit = defineEmits<{ close: []; logout: [] }>();

const VISIBLE_ACCOUNT_LIMIT = 3;
const ACCOUNT_MENU_TRIGGER_KEY = 'account-switcher-more';
const ACCOUNT_MENU_HEADER_KEY = 'account-switcher-all-accounts';

interface AccountMenuItem extends MenuItem {
  account: AccountSwitcherItem;
  key: string;
  label: string;
}

const accountMenuPassThrough = {
  root: { class: 'app-account-switcher__menu' },
  rootList: { class: 'app-account-switcher__menu-list app-account-switcher__menu-list--root' },
  item: { class: 'app-account-switcher__menu-item' },
  itemContent: { class: 'app-account-switcher__menu-item-content' },
  itemLink: { class: 'app-account-switcher__menu-action' },
  itemIcon: { class: 'app-account-switcher__menu-icon' },
  itemLabel: { class: 'app-account-switcher__menu-label' },
  submenu: { class: 'app-account-switcher__menu-list app-account-switcher__menu-list--submenu' },
} satisfies TieredMenuPassThroughOptions;

const { t } = useTranslation();
const router = useRouter();
const { accounts, isSwitchingAccount, switchAccount, switchError } = useAccountSwitcher();

const authSessionStore = useAuthSessionStore();
const activeIdentity = useActiveIdentity();

const userName = computed(() =>
  [authSessionStore.givenName, authSessionStore.familyName].filter(Boolean).join(' ').trim(),
);
const userEmail = computed(() => activeIdentity.email.value ?? '');
const userInitial = computed(() => userName.value.trim().charAt(0).toUpperCase());

const hasAccounts = computed(() => accounts.value.length > 0);
const visibleAccounts = computed(() => accounts.value.slice(0, VISIBLE_ACCOUNT_LIMIT));
const hasOverflowAccounts = computed(() => accounts.value.length > VISIBLE_ACCOUNT_LIMIT);
const accountMenuItems = computed<AccountMenuItem[]>(() =>
  accounts.value.map((account) => {
    const menuItem: AccountMenuItem = {
      account,
      key: account.id,
      label: account.label,
      disabled: account.isSelected || isSwitchingAccount.value,
      command: () => {
        void switchAccount(account.id);
      },
    };

    return menuItem;
  }),
);
const accountOverflowMenuItems = computed<MenuItem[]>(() => [
  {
    key: ACCOUNT_MENU_TRIGGER_KEY,
    label: t('nav.view_all_profiles', { total: accounts.value.length }),
    disabled: isSwitchingAccount.value,
    items: [
      {
        key: ACCOUNT_MENU_HEADER_KEY,
        label: t('nav.all_profiles'),
        disabled: true,
      },
      ...accountMenuItems.value,
    ],
  },
]);

const isAccountMenuItem = (item: MenuItem): item is AccountMenuItem => {
  return 'account' in item;
};

const isAccountMenuHeader = (item: MenuItem) => {
  return item.key === ACCOUNT_MENU_HEADER_KEY;
};

const navigateTo = (name: string) => {
  emit('close');
  void router.push({ name });
};

const handleLogout = () => {
  emit('close');
  emit('logout');
};
</script>

<template>
  <section class="app-account-switcher" data-test="account-switcher" data-testid="account-switcher">
    <div
      v-if="userName || userEmail"
      class="app-account-switcher__profile"
      data-test="account-switcher-profile"
      data-testid="account-switcher-profile"
    >
      <span class="app-account-switcher__profile-avatar" aria-hidden="true">{{ userInitial }}</span>
      <span class="app-account-switcher__profile-text">
        <span class="app-account-switcher__profile-name">{{ userName }}</span>
        <span class="app-account-switcher__profile-email">{{ userEmail }}</span>
      </span>
    </div>

    <hr class="app-account-switcher__divider" />

    <div v-if="hasAccounts" class="app-account-switcher__switch">
      <p class="app-account-switcher__title">{{ t('nav.switch_profiles') }}</p>

      <div class="app-account-switcher__list">
        <Button
          v-for="account in visibleAccounts"
          :key="account.id"
          type="button"
          variant="text"
          class="app-account-switcher__account app-account-switcher__item"
          :class="{ 'app-account-switcher__account--selected': account.isSelected }"
          :disabled="account.isSelected || isSwitchingAccount"
          :data-account-id="account.id"
          data-test="account-switcher-item"
          data-testid="account-switcher-item"
          @click="switchAccount(account.id)"
        >
          <span class="app-account-switcher__account-content">
            <span class="app-account-switcher__avatar" aria-hidden="true">
              {{ account.initial }}
            </span>
            <span class="app-account-switcher__account-label">{{ account.label }}</span>
          </span>

          <span
            v-if="account.isSelected"
            class="app-account-switcher__account-check pi pi-check"
            aria-hidden="true"
            data-test="account-switcher-check"
            data-testid="account-switcher-check"
          />
        </Button>

        <TieredMenu
          v-if="hasOverflowAccounts"
          id="account_switcher_accounts_menu"
          class="app-account-switcher__more-menu"
          :model="accountOverflowMenuItems"
          :pt="accountMenuPassThrough"
          unstyled
        >
          <template #item="{ item, label, props }">
            <span
              v-if="isAccountMenuHeader(item)"
              class="app-account-switcher__menu-heading"
              data-test="account-switcher-menu-title"
              data-testid="account-switcher-menu-title"
            >
              {{ label }}
            </span>

            <a
              v-else-if="isAccountMenuItem(item)"
              v-bind="props.action"
              class="app-account-switcher__account"
              data-test="account-switcher-menu-item"
              data-testid="account-switcher-menu-item"
              :data-account-id="item.account.id"
            >
              <span class="app-account-switcher__account-content">
                <span class="app-account-switcher__avatar" aria-hidden="true">
                  {{ item.account.initial }}
                </span>
                <span class="app-account-switcher__account-label">{{ item.account.label }}</span>
              </span>

              <span
                v-if="item.account.isSelected"
                class="app-account-switcher__account-check pi pi-check"
                aria-hidden="true"
                data-test="account-switcher-check"
                data-testid="account-switcher-check"
              />
            </a>
            <a
              v-else
              v-bind="props.action"
              class="app-account-switcher__account"
              data-test="account-switcher-more"
              data-testid="account-switcher-more"
            >
              <span class="app-account-switcher__account-content">
                <ListNarrowIcon class="app-account-switcher__menu-icon" aria-hidden="true" />
                <span v-bind="props.label" class="app-account-switcher__account-label">
                  {{ label }}
                </span>
              </span>
            </a>
          </template>
        </TieredMenu>

        <Button
          type="button"
          variant="text"
          class="app-account-switcher__account app-account-switcher__apply-account"
          data-test="account-switcher-apply-new-account"
          data-testid="account-switcher-apply-new-account"
          @click="navigateTo(authRouteNames.createOrganization)"
        >
          <span class="app-account-switcher__account-label">
            {{ t('nav.apply_new_profile') }}
          </span>
        </Button>
      </div>
    </div>

    <hr v-if="hasAccounts" class="app-account-switcher__divider" />

    <div class="app-account-switcher__menu-section">
      <p class="app-account-switcher__title">{{ t('nav.menu') }}</p>

      <Button
        type="button"
        variant="text"
        class="app-account-switcher__account app-account-switcher__link"
        data-test="account-switcher-user-management"
        data-testid="account-switcher-user-management"
        @click="navigateTo('settings-user-control')"
      >
        <span class="app-account-switcher__account-content">
          <Users2Icon class="app-account-switcher__link-icon" aria-hidden="true" />
          <span class="app-account-switcher__account-label">{{ t('nav.user_management') }}</span>
        </span>
      </Button>

      <Button
        type="button"
        variant="text"
        class="app-account-switcher__account app-account-switcher__link"
        data-test="account-switcher-analytics"
        data-testid="account-switcher-analytics"
        @click="navigateTo('analytics')"
      >
        <span class="app-account-switcher__account-content">
          <LineChartIcon class="app-account-switcher__link-icon" aria-hidden="true" />
          <span class="app-account-switcher__account-label">{{ t('nav.analytics') }}</span>
        </span>
      </Button>

      <Button
        type="button"
        variant="text"
        class="app-account-switcher__account app-account-switcher__link app-account-switcher__settings"
        data-test="account-switcher-settings"
        data-testid="account-switcher-settings"
        @click="navigateTo('settings')"
      >
        <span class="app-account-switcher__account-content">
          <SettingsIcon class="app-account-switcher__link-icon" aria-hidden="true" />
          <span class="app-account-switcher__account-label">{{ t('nav.settings') }}</span>
        </span>
      </Button>
    </div>

    <hr class="app-account-switcher__divider" />

    <Button
      type="button"
      variant="text"
      class="app-account-switcher__account app-account-switcher__link app-account-switcher__logout"
      data-test="account-switcher-logout"
      data-testid="account-switcher-logout"
      @click="handleLogout"
    >
      <span class="app-account-switcher__account-content">
        <LogOutIcon class="app-account-switcher__link-icon" aria-hidden="true" />
        <span class="app-account-switcher__account-label">{{ t('nav.logout') }}</span>
      </span>
    </Button>

    <p v-if="switchError" class="app-account-switcher__error" role="alert">
      {{ t('nav.switch_profile_failed') }}
    </p>
  </section>
</template>

<style scoped lang="scss">
.app-account-switcher {
  display: flex;
  flex-direction: column;
  gap: $static-space-2;

  &__divider {
    width: 100%;
    margin: 0;
    border: 0;
    border-top: 1px solid var(--p-content-border-color);
  }

  &__profile {
    display: flex;
    gap: $spacing-s;
    align-items: center;
    padding: 6px $spacing-s;
    border-radius: $radius-8;
  }

  &__profile-avatar {
    display: inline-flex;
    flex: 0 0 36px;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: $radius-999;
    color: $color-text-inverse;
    background: $color-base-surface-200;
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    line-height: 1;
  }

  &__profile-text {
    display: flex;
    flex-direction: column;
    gap: $spacing-2xs;
    min-width: 0;
  }

  &__profile-name {
    color: $color-text-primary;
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    line-height: 1;
  }

  &__profile-email {
    overflow: hidden;
    color: $color-text-muted;
    font-size: $font-size-xs;
    font-weight: $font-weight-regular;
    line-height: $line-height-xs;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__switch {
    display: flex;
    flex-direction: column;
    gap: $static-space-2;
  }

  &__menu-section {
    display: flex;
    flex-direction: column;
    gap: $spacing-2xs;
  }

  &__link-icon {
    flex: 0 0 $icon-size-xs;
    width: $icon-size-xs;
    height: $icon-size-xs;
    color: $color-icon-muted;
  }

  &__title {
    margin: 0;
    padding: 0 $static-space-2;
    color: var(--p-surface-500);
    font-size: $text-xs;
    font-weight: $font-weight-regular;
    line-height: $leading-tight;
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__account {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    height: calc($static-space-8 + $static-space-1);
    min-height: calc($static-space-8 + $static-space-1);
    gap: $static-space-3;
    padding: $static-space-2 $static-space-4 $static-space-2 $static-space-2;
    border: 0;
    border-radius: $radius-8;
    color: var(--p-surface-900);
    box-sizing: border-box;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    &:not(:disabled):hover {
      background: var(--p-menu-item-focus-background);
    }

    &:disabled {
      cursor: default;
      opacity: 1;
    }
  }

  &__account-content {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    gap: $static-space-2;
  }

  &__avatar {
    display: inline-flex;
    flex: 0 0 $static-space-6;
    align-items: center;
    justify-content: center;
    width: $static-space-6;
    height: $static-space-6;
    overflow: hidden;
    border-radius: $radius-999;
    color: var(--p-surface-0);
    background: var(--p-surface-700);
    font-size: $text-sm;
    font-weight: $font-weight-semibold;
    line-height: $leading-none;
  }

  &__account-label {
    min-width: 0;
    overflow: hidden;
    color: var(--p-surface-900);
    font-size: $text-sm;
    font-weight: $font-weight-regular;
    line-height: $leading-tight;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__account-check {
    flex: 0 0 $static-space-4;
    color: $color-state-success;
    font-size: $static-space-4;
  }

  &__menu-heading {
    display: block;
    color: var(--p-surface-500);
    font-size: $text-xs;
    font-weight: $font-weight-semibold;
    line-height: $leading-tight;
    padding: $static-space-3 $static-space-3 $static-space-2 $static-space-2;
  }

  &__apply-account {
    margin: 0 $static-space-2 $static-space-1 0;
  }

  &__error {
    margin: 0;
    padding: 0 $static-space-2;
    color: var(--p-message-error-color);
    font-size: $text-xs;
    line-height: $leading-tight;
  }
}

.app-account-switcher__more-menu {
  width: 100%;
  min-width: 0;
  background: transparent;
}

:deep(.app-account-switcher__menu-list) {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

:deep(.app-account-switcher__menu-list--submenu) {
  position: absolute;
  inset-inline-start: auto !important;
  inset-inline-end: calc(100% + #{$static-space-3});
  z-index: 1;
  min-width: 100%;
  border: 1px solid var(--p-content-border-color);
  border-radius: $radius-8;
  background: var(--p-surface-0);
  box-shadow: none;
  box-sizing: border-box;
  padding: 0 $static-space-1;
}

:deep(.app-account-switcher__menu-item) {
  position: relative;
}

:deep(.app-account-switcher__menu-item[data-p-disabled='true']) {
  pointer-events: none;
}

:deep(.app-account-switcher__menu-item[data-p-disabled='true'] .app-account-switcher__account) {
  cursor: default;
}

:deep(.app-account-switcher__menu-icon) {
  flex: 0 0 $static-space-4;
  width: $static-space-4;
  height: $static-space-4;
  color: $color-state-success;
  font-size: $static-space-4;
  margin-left: $static-space-1;
  margin-right: $static-space-1;
}

:deep(.app-account-switcher__menu-label) {
  min-width: 0;
}
</style>
